import {
  AcousticProtocol,
  AcousticSender,
  defaultConfig,
  type AcousticConfig,
} from '../../core';
import { Scope } from '../components/Scope';
import { SoftKey } from '../components/SoftKey';
import { LedMeter } from '../components/LedMeter';
import { SymbolStream, type SymbolKind } from '../components/SymbolStream';

type ConfigGetter = () => Partial<AcousticConfig>;
type LogFn = (msg: string) => void;

const COUNTER_WARN_RATIO = 0.75;

export class SenderView {
  readonly el: HTMLElement;
  private scope: Scope;
  private meter: LedMeter;
  private symbolStream: SymbolStream;
  private composeBtn: SoftKey;
  private sendBtn: SoftKey;
  private exportBtn: SoftKey;

  private composeWrap: HTMLDivElement;
  private textarea: HTMLTextAreaElement;
  private counterEl: HTMLSpanElement;
  private metaDuration: HTMLSpanElement;
  private statusBadge: HTMLSpanElement;
  private progressFill: HTMLDivElement;
  private progressValue: HTMLSpanElement;

  private preambleEndIdx = 0;
  private syncEndIdx = 0;
  private dataEndIdx = 0;

  private metaDebounce: number | null = null;
  private metaEpoch = 0;
  private overLimit = false;
  private exporting = false;

  readonly sender = new AcousticSender();
  private getConfig: ConfigGetter;
  private log: LogFn;
  private analyserPoll: number | null = null;

  constructor(getConfig: ConfigGetter, log: LogFn) {
    this.getConfig = getConfig;
    this.log = log;

    this.el = document.createElement('section');
    this.el.className = 'panel';
    this.el.dataset.kind = 'sender';

    const body = document.createElement('div');
    body.className = 'panel__body';

    this.composeWrap = document.createElement('div');
    this.composeWrap.className = 'tx-compose';
    this.composeWrap.dataset.state = 'idle';

    const composeBar = document.createElement('div');
    composeBar.className = 'tx-compose__bar';

    this.composeBtn = new SoftKey('TX FRAME', 'primary');
    this.composeBtn.el.classList.add('tx-compose__open-btn');

    this.statusBadge = document.createElement('span');
    this.statusBadge.className = 'panel-state';
    this.statusBadge.dataset.state = 'idle';
    this.statusBadge.textContent = 'IDLE';

    composeBar.appendChild(this.composeBtn.el);
    composeBar.appendChild(this.statusBadge);
    this.composeWrap.appendChild(composeBar);

    const composePanel = document.createElement('div');
    composePanel.className = 'tx-compose__panel';

    composePanel.innerHTML = `
      <div class="tx-compose__row">
        <span class="form-label">Payload</span>
        <span class="tx-compose__close" role="button" tabindex="0" aria-label="Close composer">×</span>
      </div>
      <textarea class="tx-input__textarea" id="tx-text"
        placeholder="Enter message…"
        rows="3" maxlength="8192" spellcheck="false"></textarea>
      <div class="tx-input__meta">
        <span id="tx-counter" class="tx-counter" data-zone="ok">0 / ${defaultConfig.maxPayload} B</span>
        <span id="tx-meta-duration">— s @ 20 sym/s</span>
      </div>
    `;
    this.composeWrap.appendChild(composePanel);

    this.textarea = composePanel.querySelector<HTMLTextAreaElement>('#tx-text')!;
    this.counterEl = composePanel.querySelector<HTMLSpanElement>('#tx-counter')!;
    this.metaDuration = composePanel.querySelector<HTMLSpanElement>('#tx-meta-duration')!;

    const composeActions = document.createElement('div');
    composeActions.className = 'tx-compose__actions';
    this.exportBtn = new SoftKey('EXPORT WAV', 'ghost');
    this.sendBtn = new SoftKey('SEND', 'arm');
    composeActions.appendChild(this.exportBtn.el);
    composeActions.appendChild(this.sendBtn.el);
    composePanel.appendChild(composeActions);

    body.appendChild(this.composeWrap);

    const progress = document.createElement('div');
    progress.className = 'progress-row';
    progress.innerHTML = `
      <div class="progress-bar"><div class="progress-bar__fill"></div></div>
      <span class="progress-row__value">0%</span>
    `;
    this.progressFill = progress.querySelector<HTMLDivElement>('.progress-bar__fill')!;
    this.progressValue = progress.querySelector<HTMLSpanElement>('.progress-row__value')!;
    body.appendChild(progress);

    const vizBlock = document.createElement('div');
    vizBlock.className = 'viz-block';
    vizBlock.innerHTML = `
      <div class="viz-block__head">
        <span class="viz-block__title">Waveform · Oszilloskop</span>
        <span class="viz-block__sub">CH1 · WAVE</span>
      </div>
      <div class="viz-block__canvas"></div>
    `;
    this.scope = new Scope();
    vizBlock.querySelector<HTMLDivElement>('.viz-block__canvas')!.appendChild(this.scope.el);
    body.appendChild(vizBlock);

    this.meter = new LedMeter();
    body.appendChild(this.meter.el);

    const streamBlock = document.createElement('div');
    streamBlock.className = 'viz-block';
    streamBlock.innerHTML = `
      <div class="viz-block__head">
        <span class="viz-block__title">Symbol Stream</span>
        <span class="viz-block__sub">4-FSK · TX</span>
      </div>
      <div class="viz-block__canvas viz-block__canvas--symbols"></div>
    `;
    this.symbolStream = new SymbolStream();
    streamBlock.querySelector<HTMLDivElement>('.viz-block__canvas')!.appendChild(this.symbolStream.el);
    body.appendChild(streamBlock);

    this.el.appendChild(body);

    this.composeBtn.onClick(() => this.toggleCompose());
    composePanel
      .querySelector<HTMLSpanElement>('.tx-compose__close')!
      .addEventListener('click', () => this.setComposeOpen(false));

    this.sendBtn.onClick(() => this.handleSend());
    this.exportBtn.onClick(() => this.handleExport());

    this.bindEvents();
    this.scope.start();
    this.meter.start();
    this.symbolStream.start();
    this.scheduleMetaUpdate();
  }

  refreshMeta(): void {
    this.scheduleMetaUpdate();
  }

  private toggleCompose(): void {
    this.setComposeOpen(!this.composeWrap.classList.contains('is-open'));
  }

  private setComposeOpen(open: boolean): void {
    this.composeWrap.classList.toggle('is-open', open);
    this.composeBtn.setLabel(open ? 'TX FRAME ▾' : 'TX FRAME');
    if (open) {
      window.setTimeout(() => this.textarea.focus(), 30);
    }
  }

  private bindEvents(): void {
    this.textarea.addEventListener('input', () => this.scheduleMetaUpdate());

    this.sender.onProgress = (current, total) => {
      const pct = total === 0 ? 0 : Math.round((current / total) * 100);
      this.progressFill.style.width = pct + '%';
      this.progressValue.textContent = pct + '%';
    };
    this.sender.onSymbolStart = (idx, symbol) => {
      this.symbolStream.pushSymbol(symbol, this.kindForIdx(idx));
    };
    this.sender.onComplete = () => {
      this.sendBtn.setLabel('SEND');
      this.sendBtn.setVariant('arm');
      this.statusBadge.dataset.state = 'complete';
      this.statusBadge.textContent = 'SENT';
      this.composeWrap.dataset.state = 'idle';
      this.log('[TX] Transmission complete');
      window.setTimeout(() => {
        this.statusBadge.dataset.state = 'idle';
        this.statusBadge.textContent = 'IDLE';
      }, 1500);
    };
    this.sender.onError = (msg) => {
      this.sendBtn.setLabel('SEND');
      this.sendBtn.setVariant('arm');
      this.statusBadge.dataset.state = 'error';
      this.statusBadge.textContent = 'ERR';
      this.composeWrap.dataset.state = 'idle';
      this.log('[TX] Error: ' + msg);
    };
  }

  private async handleSend(): Promise<void> {
    if (this.sender.sending) {
      this.sender.stop();
      this.sendBtn.setLabel('SEND');
      this.sendBtn.setVariant('arm');
      this.statusBadge.dataset.state = 'idle';
      this.statusBadge.textContent = 'IDLE';
      this.composeWrap.dataset.state = 'idle';
      this.log('[TX] Aborted');
      this.scope.bind(null);
      this.meter.bind(null);
      return;
    }
    if (this.overLimit) return;
    const text = this.textarea.value.trim();
    if (!text) return;
    const config = this.readConfig();
    const cfg = { ...defaultConfig, ...config };
    let symbols: number[];
    try {
      symbols = await AcousticProtocol.encodeFrame(text, cfg);
    } catch (e) {
      this.log('[TX] encodeFrame failed: ' + (e as Error).message);
      this.statusBadge.dataset.state = 'error';
      this.statusBadge.textContent = 'ERR';
      return;
    }
    const baud = config.baudRate ?? defaultConfig.baudRate;
    const duration = (symbols.length / baud).toFixed(1);

    this.preambleEndIdx = cfg.preamble.length;
    this.syncEndIdx = this.preambleEndIdx + cfg.syncWord.length;
    this.dataEndIdx = symbols.length - cfg.endWord.length;
    this.symbolStream.clear();
    this.log(`[TX] Send "${text}"`);
    this.log(`[TX] ${symbols.length} symbols, ~${duration}s @ ${baud} sym/s`);
    this.log(`[TX] Frequencies: [${(config.freqs ?? defaultConfig.freqs).join(', ')}] Hz`);

    this.sendBtn.setLabel('STOP');
    this.sendBtn.setVariant('abort');
    this.statusBadge.dataset.state = 'receiving';
    this.statusBadge.textContent = 'TX';
    this.composeWrap.dataset.state = 'active';
    this.setComposeOpen(false);

    this.sender.send(text, config).catch((err) => {
      this.log('[TX] send() rejected: ' + (err as Error).message);
      this.statusBadge.dataset.state = 'error';
      this.statusBadge.textContent = 'ERR';
      this.composeWrap.dataset.state = 'idle';
      this.sendBtn.setLabel('SEND');
      this.sendBtn.setVariant('arm');
    });
    this.pollAnalyser();
  }

  private async handleExport(): Promise<void> {
    if (this.exporting || this.overLimit) return;
    const text = this.textarea.value.trim();
    if (!text) return;
    this.exporting = true;
    const previousLabel = 'EXPORT WAV';
    this.exportBtn.setLabel('RENDERING…');
    this.exportBtn.setDisabled(true);
    try {
      const config = this.readConfig();
      const blob = await this.sender.renderToWav(text, config);
      const filename = makeWavFilename(new Date());
      triggerDownload(blob, filename);
      this.log(`[TX] Exported ${filename} (${formatBytes(blob.size)})`);
    } catch (e) {
      this.log('[TX] Export failed: ' + (e as Error).message);
      this.statusBadge.dataset.state = 'error';
      this.statusBadge.textContent = 'ERR';
    } finally {
      this.exportBtn.setLabel(previousLabel);
      this.exportBtn.setDisabled(this.overLimit);
      this.exporting = false;
    }
  }

  private pollAnalyser(): void {
    if (this.analyserPoll !== null) window.clearInterval(this.analyserPoll);
    let attempts = 0;
    this.analyserPoll = window.setInterval(() => {
      const a = this.sender.getAnalyser();
      if (a) {
        this.scope.bind(a);
        this.meter.bind(a);
        if (this.analyserPoll !== null) window.clearInterval(this.analyserPoll);
        this.analyserPoll = null;
        return;
      }
      if (++attempts > 100 && this.analyserPoll !== null) {
        window.clearInterval(this.analyserPoll);
        this.analyserPoll = null;
      }
    }, 50);
  }

  readConfig(): Partial<AcousticConfig> {
    return { ...this.getConfig() };
  }

  private kindForIdx(idx: number): SymbolKind {
    if (idx < this.preambleEndIdx) return 'preamble';
    if (idx < this.syncEndIdx) return 'sync';
    if (idx < this.dataEndIdx) return 'data';
    return 'end';
  }

  private scheduleMetaUpdate(): void {
    if (this.metaDebounce !== null) window.clearTimeout(this.metaDebounce);
    this.metaDebounce = window.setTimeout(() => this.updateMetaNow(), 100);
  }

  private async updateMetaNow(): Promise<void> {
    const text = this.textarea.value;
    const cfg = this.getConfig();
    const baud = cfg.baudRate ?? defaultConfig.baudRate;
    const limit = cfg.maxPayload ?? defaultConfig.maxPayload;
    const epoch = ++this.metaEpoch;

    if (text.length === 0) {
      this.applyCounter(0, limit, 'raw');
      this.metaDuration.textContent = `— s @ ${baud} sym/s`;
      return;
    }

    try {
      const estimate = await AcousticProtocol.estimateFrameBytes(text);
      if (epoch !== this.metaEpoch) return;
      this.applyCounter(estimate.transmittedBytes, limit, estimate.variant);
      if (estimate.transmittedBytes <= limit) {
        const symbols = await AcousticProtocol.encodeFrame(text, {
          ...defaultConfig,
          ...cfg,
        });
        if (epoch !== this.metaEpoch) return;
        const duration = (symbols.length / baud).toFixed(1);
        this.metaDuration.textContent = `${symbols.length} sym · ~${duration} s @ ${baud} sym/s`;
      } else {
        this.metaDuration.textContent = `Limit exceeded — ${estimate.transmittedBytes - limit} B over`;
      }
    } catch (e) {
      if (epoch !== this.metaEpoch) return;
      this.metaDuration.textContent = (e as Error).message;
    }
  }

  private applyCounter(
    transmitted: number,
    limit: number,
    variant: 'raw' | 'deflate',
  ): void {
    this.counterEl.textContent = `${transmitted} / ${limit} B (${variant})`;
    let zone: 'ok' | 'warn' | 'over';
    if (transmitted > limit) zone = 'over';
    else if (transmitted >= limit * COUNTER_WARN_RATIO) zone = 'warn';
    else zone = 'ok';
    this.counterEl.dataset.zone = zone;
    const wasOver = this.overLimit;
    this.overLimit = zone === 'over';
    if (wasOver !== this.overLimit) {
      this.sendBtn.setDisabled(this.overLimit || this.sender.sending);
      this.exportBtn.setDisabled(this.overLimit || this.exporting);
    }
  }
}

function pad2(n: number): string {
  return n < 10 ? '0' + n : '' + n;
}

function makeWavFilename(d: Date): string {
  const date = `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
  const time = `${pad2(d.getHours())}${pad2(d.getMinutes())}`;
  return `tx_${date}_${time}.wav`;
}

function triggerDownload(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.rel = 'noopener';
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(url), 4000);
}

function formatBytes(n: number): string {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / 1024 / 1024).toFixed(2)} MB`;
}
